import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, Text, TouchField } from 'components';
import { FontAwesome5 } from '@expo/vector-icons';
import { StyleSheet, Animated, Dimensions, Easing } from 'react-native';
import Client from 'store/api-client';
import { ILiveTickerEvent } from 'type';

const MSC_BLUE = '#004B8D';
const MSC_BLUE_LIGHT = '#0066B3';
const WARNING = '#F5A623';
const SUCCESS = '#1FA463';
const ERROR = '#E94A36';

// Constant scroll speed in pixels per second
const SCROLL_SPEED = 100; // pixels per second

interface LiveTickerProps {
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

const LiveTicker: React.FC<LiveTickerProps> = ({ isFullscreen = false, onToggleFullscreen }) => {
  const [tickerItems, setTickerItems] = useState<ILiveTickerEvent[]>([]);
  const scrollX = useRef(new Animated.Value(0)).current;
  const { width } = Dimensions.get('window');

  useEffect(() => {
    const fetchTickerEvents = async () => {
      try {
        const res = await Client.Api.Operations.liveTickerEvents({
          limit: 20,
          hours: 4,
        });

        if (res.data.success && res.data.data?.events) {
          setTickerItems(res.data.data.events);
        }
      } catch (error) {
        console.error('Error fetching ticker events:', error);
      }
    };

    fetchTickerEvents();

    const interval = setInterval(fetchTickerEvents, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (tickerItems.length === 0) return;

    // Calculate total width needed (all items + spacing)
    const itemWidth = width * 0.6; // Approximate width per item
    const spacing = 48; // mx-12 equivalent
    const totalWidth = tickerItems.length * (itemWidth + spacing * 2);

    // Calculate duration based on constant speed
    // duration (ms) = distance (px) / speed (px/s) * 1000
    const duration = (totalWidth / SCROLL_SPEED) * 1000;

    // Create infinite scroll animation
    const animate = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(scrollX, {
            toValue: -totalWidth,
            duration: duration,
            useNativeDriver: true,
            easing: Easing.linear,
          }),
          Animated.timing(scrollX, {
            toValue: 0,
            duration: 0,
            useNativeDriver: true,
          }),
        ])
      ).start();
    };

    animate();
  }, [width, tickerItems.length]);

  return (
    <Col style={styles.footerTicker}>
      {/* Ticker Label */}
      <Col style={styles.tickerLabel}>
        <Col style={styles.labelIcon}>
          <FontAwesome5 name="broadcast-tower" size={14} color="white" />
        </Col>
        <Text fontSize={14} bold color="white" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>
          LIVE TICKER
        </Text>
      </Col>

      {/* Scrolling Ticker Content */}
      <Col flex1 style={styles.tickerContent}>
        <Animated.View
          style={[
            styles.tickerScroll,
            {
              transform: [{ translateX: scrollX }],
            },
          ]}
        >
          {tickerItems.length > 0 ? (
            <>
              {tickerItems.map((item, index) => (
                <Row
                  key={`${item.id}-${index}`}
                  alignItems="center"
                  style={styles.tickerItem}
                >
                  <Col style={[styles.tickerIcon, { backgroundColor: item.iconColor }]}>
                    <FontAwesome5 name={item.icon as any} size={12} color="white" />
                  </Col>
                  <Text fontSize={18} semiBold color="white">
                    {item.message}
                  </Text>
                </Row>
              ))}
              {/* Duplicate items for seamless loop */}
              {tickerItems.map((item, index) => (
                <Row
                  key={`${item.id}-dup-${index}`}
                  alignItems="center"
                  style={styles.tickerItem}
                >
                  <Col style={[styles.tickerIcon, { backgroundColor: item.iconColor }]}>
                    <FontAwesome5 name={item.icon as any} size={12} color="white" />
                  </Col>
                  <Text fontSize={18} semiBold color="white">
                    {item.message}
                  </Text>
                </Row>
              ))}
            </>
          ) : (
            <Row alignItems="center" style={styles.tickerItem}>
              <Text fontSize={18} semiBold color="white">
                Loading live updates...
              </Text>
            </Row>
          )}
        </Animated.View>
      </Col>

      {/* Gradient Fade Effects */}
      {/* <Col style={styles.gradientLeft} pointerEvents="none" />
      <Col style={styles.gradientRight} pointerEvents="none" /> */}
      
      {/* Fullscreen Toggle Button */}
      {onToggleFullscreen && (
        <TouchField
          onPress={onToggleFullscreen}
          style={styles.fullscreenButton}
          cirle
          backgroundColor="rgba(0, 75, 141, 0.9)"
          bgHovered="rgba(0, 75, 141, 1)"
        >
          <Col flex1 justifyContent="center" alignItems="center">
            <FontAwesome5 
              name={isFullscreen ? "compress" : "expand"} 
              size={18} 
              color="white" 
            />
          </Col>
        </TouchField>
      )}
    </Col>
  );
};

const styles = StyleSheet.create({
  footerTicker: {
    height: 80,
    backgroundColor: MSC_BLUE,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
    borderTopWidth: 2,
    borderTopColor: MSC_BLUE_LIGHT,
    position: 'relative',
  },
  tickerLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    height: '100%',
    borderRightWidth: 1,
    borderRightColor: 'rgba(255, 255, 255, 0.2)',
    minWidth: 180,
  },
  labelIcon: {
    width: 40,
    height: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    marginRight: 16,
  },
  tickerContent: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
  },
  tickerScroll: {
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    position: 'absolute',
    left: 0,
    right: 0,
  },
  tickerItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 20,
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    marginHorizontal: 48,
    flexShrink: 0,
  },
  tickerIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  gradientLeft: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 128,
    height: '100%',
    backgroundColor: MSC_BLUE,
    opacity: 0.8,
  },
  gradientRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 128,
    height: '100%',
    backgroundColor: MSC_BLUE,
    opacity: 0.8,
  },
  fullscreenButton: {
    position: 'absolute',
    right: 16,
    top: '50%',
    marginTop: -24, // Half of button height to center vertically
    width: 40,
    height: 40,
    borderRadius: 20,
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
});

export default LiveTicker;

